<!--
  模糊 + 精准二合一自动完成（字段可配置，互斥传参）
  输入联想行为与 wms-auto-complate 完全一致
  - 手动输入：传 fuzzyField，exactField 为 null
  - 下拉选中：传 exactField，fuzzyField 为 null
-->
<template>
  <component
    :is="wrapperTag"
    :span="span || undefined"
    class="wms-dual-auto-complate">
    <wms-form-item
      v-show="false"
      class="wms-dual-auto-complate__exact-prop"
      :prop="exactField"
      :rules="exactFieldRules"
      label-width="0">
      <el-input :value="exactValue == null ? '' : exactValue" readonly tabindex="-1" />
    </wms-form-item>
    <wms-auto-complate
      :span="innerSpan"
      :unFormIs="unFormIs"
      :label="label"
      :label-width="labelWidth"
      :prop="resolvedProp"

      :rules="mergedRules"
      :error="error"
      :show-message="showMessage"
      :inline-message="inlineMessage"
      :form-item-size="formItemSize"
      :size="size"
      :placeholder="placeholder"
      :disabled="disabled"
      :value-key="valueKey"
      :value-keys="valueKeys"
      v-model="displayValue"
      :type="type"
      :debounce="debounce"
      :placement="placement"
      :use-suggest="useSuggest"
      :fetch-suggestions="fetchSuggestions"
      :popper-class="popperClass"
      :trigger-on-focus="triggerOnFocus"
      :name="name"
      :select-when-unmatched="selectWhenUnmatched"
      :prefix-icon="prefixIcon"
      :suffix-icon="suffixIcon"
      :hide-loading="hideLoading"
      :popper-append-to-body="popperAppendToBody"
      :highlight-first-item="highlightFirstItem"
      :have-slot-scope="(unFormIs || innerSpan !== 0) && (haveSlotScope || slotScope)"
      :slot-scope="!unFormIs && innerSpan === 0 && (haveSlotScope || slotScope)"
      :option-list="optionList"
      :clearable="clearable"
      @select="handleSelect"
      @clear="handleClear"
      @change="handleChange"
      @focusInput="$emit('focusInput', $event)"
    >
      <template v-if="haveSlotScope || slotScope" slot-scope="scope">
        <slot :option="scope.option">
          <div class="name">{{ scope.option[exactField] }}</div>
          <div class="addr">{{ scope.option[valueKey] }}</div>
        </slot>
      </template>
      <template slot="prefix">
        <slot name="prefix" />
      </template>
      <template slot="suffix">
        <slot name="suffix" />
      </template>
      <template slot="prepend">
        <slot name="prepend" />
      </template>
      <template slot="append">
        <slot name="append" />
      </template>
    </wms-auto-complate>
  </component>
</template>

<script>
export default {
  name: 'WmsDualAutoComplate',
  props: {
    fuzzyField: {
      type: String,
      required: true
    },
    exactField: {
      type: String,
      required: true
    },
    fuzzyValue: {
      type: [String, Number],
      default: null
    },
    exactValue: {
      type: [String, Number],
      default: null
    },
    unFormIs: {
      type: Boolean,
      default: false
    },
    span: {
      type: Number,
      default: 0
    },
    prop: {
      type: String
    },
    label: String,
    labelWidth: String,
    required: Boolean,
    rules: [Object, Array],
    error: String,
    showMessage: {
      type: Boolean,
      default: true
    },
    inlineMessage: {
      type: Boolean,
      default: false
    },
    formItemSize: {
      type: String,
      default: 'mini'
    },
    size: String,
    placeholder: String,
    disabled: {
      type: Boolean,
      default: false
    },
    valueKey: {
      type: String,
      default: 'value'
    },
    valueKeys: {
      type: Array,
      default: () => []
    },
    type: {
      type: String,
      default: 'text'
    },
    debounce: {
      type: Number,
      default: 300
    },
    placement: {
      type: String,
      default: 'bottom-start'
    },
    useSuggest: {
      type: Boolean,
      default: true
    },
    fetchSuggestions: Function,
    popperClass: String,
    triggerOnFocus: {
      type: Boolean,
      default: true
    },
    name: String,
    selectWhenUnmatched: {
      type: Boolean,
      default: false
    },
    prefixIcon: String,
    suffixIcon: String,
    hideLoading: {
      type: Boolean,
      default: false
    },
    popperAppendToBody: {
      type: Boolean,
      default: true
    },
    highlightFirstItem: {
      type: Boolean,
      default: false
    },
    haveSlotScope: {
      type: Boolean,
      default: false
    },
    slotScope: {
      type: Boolean,
      default: false
    },
    optionList: {
      type: Array,
      default: () => []
    },
    clearable: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      internalDisplay: '',
      searchMode: null,
      isSelecting: false
    }
  },
  computed: {
    wrapperTag() {
      return this.span ? 'el-col' : 'div'
    },
    innerSpan() {
      return this.span ? 0 : this.span
    },
    resolvedProp() {
      return this.prop || this.fuzzyField
    },
    /**
     * 为隐藏的 exactField 的 form-item 生成规则：
     * 如果组件 props 或父级 el-form 对 exactField 有 required 规则，
     * 则替换为自定义校验器，只要 fuzzyValue 或 exactValue 任一有值即可通过。
     * 如果没有 required，返回空数组以覆盖父级 form 的规则，防止直接校验 exactField。
     */
    exactFieldRules() {
      const hasRequired = this.required
        || this._rulesHaveRequired(this.rules)
        || this._formHasRequiredRule(this.exactField)
      if (!hasRequired) {
        // 返回空数组覆盖父级 form rules，防止对 exactField 单独做 required 校验
        return []
      }
      return [{
        validator: (rule, value, callback) => {
          const hasFuzzy = this.fuzzyValue != null && this.fuzzyValue !== ''
          const hasExact = this.exactValue != null && this.exactValue !== ''
          if (hasFuzzy || hasExact) {
            callback()
          } else {
            callback(new Error('该输入项为必填项'))
          }
        },
        trigger: 'change'
      }]
    },
    /**
     * 为可见的 fuzzyField（resolvedProp）form-item 合并规则：
     * 把用户传入的 rules 中的 required 规则替换为双字段感知的自定义校验器。
     */
    mergedRules() {
      const baseRules = Array.isArray(this.rules)
        ? this.rules
        : this.rules
          ? [this.rules]
          : []
      // 如果没有 required 标识且规则中也没有 required，也检查 form 级别
      if (!this.required && !this._rulesHaveRequired(this.rules) && !this._formHasRequiredRule(this.resolvedProp)) {
        return this.rules
      }
      // 过滤掉原始的 required 规则，替换成双字段感知的版本
      const filtered = baseRules.filter(r => !r.required)
      filtered.push({
        required: true,
        validator: (rule, value, callback) => {
          const hasFuzzy = this.fuzzyValue != null && this.fuzzyValue !== ''
          const hasExact = this.exactValue != null && this.exactValue !== ''
          if (hasFuzzy || hasExact) {
            callback()
          } else {
            callback(new Error('该输入项为必填项'))
          }
        },
        trigger: 'change'
      })
      return filtered
    },
    displayValue: {
      get() {
        return this.internalDisplay
      },
      set(val) {
        this.internalDisplay = val
        if (this.isSelecting) return
        const trimmed = (val || '').trim()
        this.searchMode = trimmed ? 'fuzzy' : null
        this.emitFields({
          [this.exactField]: null,
          [this.fuzzyField]: trimmed || null
        })
      }
    }
  },
  watch: {
    fuzzyValue() {
      this.syncDisplayFromProps()
    },
    exactValue() {
      this.syncDisplayFromProps()
    }
  },
  mounted() {
    this.syncDisplayFromProps()
  },
  methods: {
    _rulesHaveRequired(rules) {
      if (!rules) return false
      const arr = Array.isArray(rules) ? rules : [rules]
      return arr.some(r => r.required)
    },
    /**
     * 沿组件树向上查找 ElForm，检查其 rules 中是否包含指定字段的 required 规则
     */
    _formHasRequiredRule(field) {
      if (!field) return false
      let parent = this.$parent
      while (parent) {
        if (parent.$options.componentName === 'ElForm' || (parent.$options.name && parent.$options.name === 'ElForm')) {
          const formRules = parent.rules
          if (formRules && formRules[field]) {
            return this._rulesHaveRequired(formRules[field])
          }
          return false
        }
        parent = parent.$parent
      }
      return false
    },
    syncDisplayFromProps() {
      const hasFuzzy = this.fuzzyValue != null && this.fuzzyValue !== ''
      const hasExact = this.exactValue != null && this.exactValue !== ''
      if (hasFuzzy) {
        this.internalDisplay = String(this.fuzzyValue)
        this.searchMode = 'fuzzy'
      } else if (hasExact) {
        this.searchMode = 'exact'
      } else {
        this.internalDisplay = ''
        this.searchMode = null
      }
    },
    emitFields(payload) {
      const fuzzyVal = Object.prototype.hasOwnProperty.call(payload, this.fuzzyField)
        ? payload[this.fuzzyField]
        : this.fuzzyValue
      const exactVal = Object.prototype.hasOwnProperty.call(payload, this.exactField)
        ? payload[this.exactField]
        : this.exactValue
      this.$emit('update:fuzzyValue', fuzzyVal)
      this.$emit('update:exactValue', exactVal)
      this.$emit('change', {
        [this.fuzzyField]: fuzzyVal,
        [this.exactField]: exactVal
      })
    },
    handleSelect(item) {
      this.isSelecting = true
      this.searchMode = 'exact'
      this.internalDisplay = item[this.valueKey] != null ? String(item[this.valueKey]) : ''
      this.emitFields({
        [this.exactField]: item[this.exactField],
        [this.fuzzyField]: null
      })
      this.$emit('select', item)
      this.$nextTick(() => {
        this.isSelecting = false
      })
    },
    handleClear(e) {
      this.reset()
      this.$emit('clear', e)
    },
    handleChange(val) {
      this.$emit('input-change', val)
    },
    /** 清空展示与双字段，表单 resetFields 后也会通过 watch 同步 */
    reset() {
      this.searchMode = null
      this.internalDisplay = ''
      this.emitFields({
        [this.exactField]: null,
        [this.fuzzyField]: null
      })
    }
  }
}
</script>

<style scoped>
.wms-dual-auto-complate__exact-prop {
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
  opacity: 0;
  margin: 0;
  padding: 0;
  pointer-events: none;
}
.name {
  line-height: 26px;
}
.addr {
  color: #8492a6;
  font-size: 13px;
  line-height: 16px;
}
</style>
